import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Loader2, Search, Building2, User, Mail, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import EmailFormHandler from '../forms/EmailFormHandler';

interface Message {
  id: string;
  type: 'user' | 'bot' | 'search' | 'form';
  content: string;
  timestamp: Date;
  searchResults?: SearchResult[];
}

interface SearchResult {
  title: string;
  snippet: string;
  link: string;
}

interface QuickReply {
  text: string;
  action: string;
}

interface ContactFormData {
  type: 'individual' | 'business';
  name: string;
  email: string;
  phone: string;
  company?: string;
  message: string;
}

const INITIAL_MESSAGE = {
  id: '0',
  type: 'bot',
  content: "Bonjour ! Je suis votre assistant virtuel. Comment puis-je vous aider aujourd'hui ?",
  timestamp: new Date()
};

const QUICK_REPLIES: QuickReply[] = [
  { text: "Produits", action: "products" },
  { text: "Prix", action: "pricing" },
  { text: "Services", action: "services" },
  { text: "Contact", action: "contact" }
];

const BOT_RESPONSES: Record<string, {
  response: string;
  followUp?: QuickReply[];
  link?: string;
}> = {
  products: {
    response: "Nous proposons plusieurs modèles de motos électriques : Terra Pro, Sunu Urban, et Terra Rural. Quel modèle vous intéresse ?",
    followUp: [
      { text: "Terra Pro", action: "terra_pro" },
      { text: "Sunu Urban", action: "sunu_urban" },
      { text: "Terra Rural", action: "terra_rural" }
    ]
  },
  terra_pro: {
    response: "La Terra Pro est notre modèle haut de gamme, idéal pour un usage professionnel. Elle offre une autonomie de 120 km et une vitesse max de 45 km/h. Souhaitez-vous plus d'informations ?",
    followUp: [
      { text: "Prix", action: "terra_pro_price" },
      { text: "Caractéristiques", action: "terra_pro_specs" },
      { text: "Essai gratuit", action: "test_drive" }
    ],
    link: "/products/terra-pro"
  },
  sunu_urban: {
    response: "La Sunu Urban est parfaite pour la ville avec son design compact et son autonomie de 80 km. Que souhaitez-vous savoir ?",
    followUp: [
      { text: "Prix", action: "sunu_urban_price" },
      { text: "Caractéristiques", action: "sunu_urban_specs" },
      { text: "Essai gratuit", action: "test_drive" }
    ],
    link: "/products/sunu-urban"
  },
  terra_rural: {
    response: "La Terra Rural est conçue pour les zones rurales avec une excellente tenue de route. Elle offre 100 km d'autonomie. Plus d'informations ?",
    followUp: [
      { text: "Prix", action: "terra_rural_price" },
      { text: "Caractéristiques", action: "terra_rural_specs" },
      { text: "Essai gratuit", action: "test_drive" }
    ],
    link: "/products/terra-rural"
  },
  pricing: {
    response: "Nous proposons des solutions de paiement flexibles, incluant l'achat comptant et le Pay-as-you-go. Quelle option vous intéresse ?",
    followUp: [
      { text: "Achat comptant", action: "cash_price" },
      { text: "Pay-as-you-go", action: "payg" }
    ]
  },
  payg: {
    response: "Notre solution Pay-as-you-go vous permet de payer votre moto en mensualités flexibles, avec un premier mois gratuit !",
    followUp: [
      { text: "Simuler un plan", action: "simulate_plan" },
      { text: "Plus d'infos", action: "payg_info" }
    ],
    link: "/pay-as-you-go"
  },
  services: {
    response: "Nous offrons plusieurs services : maintenance, recharge, formation. Quel service vous intéresse ?",
    followUp: [
      { text: "Maintenance", action: "maintenance" },
      { text: "Recharge", action: "charging" },
      { text: "Formation", action: "training" }
    ],
    link: "/services"
  },
  contact: {
    response: "Vous pouvez nous contacter par :\n• Téléphone : +221 77 123 45 67\n• Email : hello@proxyemobility.com\n• Ou visitez notre showroom",
    followUp: [
      { text: "Localisation", action: "location" },
      { text: "Horaires", action: "hours" }
    ],
    link: "/contact"
  },
  test_drive: {
    response: "Excellent choix ! Nous proposons des essais gratuits de 24h. Souhaitez-vous prendre rendez-vous ?",
    followUp: [
      { text: "Oui, prendre RDV", action: "schedule_test" },
      { text: "Plus tard", action: "later" }
    ]
  },
  schedule_test: {
    response: "Je vais vous mettre en relation avec un conseiller qui vous contactera dans les plus brefs délais pour organiser votre essai.",
    followUp: [
      { text: "Merci !", action: "thanks" }
    ]
  },
  unknown: {
    response: "Je ne suis pas sûr de comprendre. Pouvez-vous reformuler ou choisir parmi ces options ?",
    followUp: QUICK_REPLIES
  }
};

const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState<ContactFormData>({
    type: 'individual',
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const searchWeb = async (query: string): Promise<SearchResult[]> => {
    // Simulated web search results based on keywords
    const results: SearchResult[] = [];
    
    if (query.toLowerCase().includes('moto') || query.toLowerCase().includes('électrique')) {
      results.push({
        title: 'Motos Électriques - Proxy Emobility',
        snippet: 'Découvrez notre gamme de motos électriques conçues pour l\'Afrique. Performance, fiabilité et durabilité.',
        link: '/products'
      });
    }
    
    if (query.toLowerCase().includes('station') || query.toLowerCase().includes('recharge')) {
      results.push({
        title: 'Stations de Recharge - Proxy Power',
        snippet: 'Notre réseau de stations de recharge couvre les principales villes. Rechargez votre moto rapidement et en toute sécurité.',
        link: '/stations'
      });
    }
    
    if (query.toLowerCase().includes('prix') || query.toLowerCase().includes('paiement')) {
      results.push({
        title: 'Solutions de Paiement Flexibles',
        snippet: 'Options de paiement adaptées à vos besoins : achat comptant ou Pay-as-you-go avec premier mois gratuit.',
        link: '/pay-as-you-go'
      });
    }

    return results;
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate bot thinking
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Try to find a predefined response
    const response = findBotResponse(inputValue.toLowerCase());
    
    if (response === BOT_RESPONSES.unknown) {
      // If no predefined response, search the web
      const searchResults = await searchWeb(inputValue);
      
      if (searchResults.length > 0) {
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'search',
          content: "Voici ce que j'ai trouvé qui pourrait vous intéresser :",
          timestamp: new Date(),
          searchResults
        }]);
      } else {
        // If no search results, offer contact form
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          type: 'bot',
          content: "Je ne trouve pas de réponse précise à votre question. Souhaitez-vous être mis en relation avec notre service client ?",
          timestamp: new Date()
        }]);
        setShowContactForm(true);
      }
    } else {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'bot',
        content: response.response,
        timestamp: new Date()
      }]);
    }
    
    setIsTyping(false);
  };

  const handleContactFormSubmit = async (e: React.FormEvent) => {
    setIsTyping(true);

    // The actual form submission is handled by EmailFormHandler
    // This is just for UI feedback
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: 'bot',
        content: "Merci pour vos informations ! Un conseiller vous contactera dans les plus brefs délais.",
        timestamp: new Date()
      }]);

      setShowContactForm(false);
      setContactForm({
        type: 'individual',
        name: '',
        email: '',
        phone: '',
        message: ''
      });

      toast.success('Votre demande a été envoyée avec succès !');
      setIsTyping(false);
    }, 1500);
  };

  const findBotResponse = (input: string) => {
    // Enhanced keyword matching
    const keywords = {
      pricing: ['prix', 'coût', 'tarif', 'paiement', 'mensualité'],
      products: ['produit', 'moto', 'véhicule', 'modèle', 'terra', 'sunu'],
      services: ['service', 'maintenance', 'réparation', 'entretien'],
      contact: ['contact', 'appeler', 'téléphone', 'email', 'adresse']
    };

    for (const [category, words] of Object.entries(keywords)) {
      if (words.some(word => input.includes(word))) {
        return BOT_RESPONSES[category];
      }
    }

    return BOT_RESPONSES.unknown;
  };

  const handleQuickReply = async (action: string) => {
    const response = BOT_RESPONSES[action] || BOT_RESPONSES.unknown;
    
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      type: 'bot',
      content: response.response,
      timestamp: new Date()
    }]);
    setIsTyping(false);
  };

  const renderMessage = (message: Message) => {
    switch (message.type) {
      case 'search':
        return (
          <div className="space-y-3 sm:space-y-4">
            <p className="mb-2">{message.content}</p>
            {message.searchResults?.map((result, index) => (
              <div key={index} className="bg-white p-2 sm:p-3 rounded-lg shadow-sm">
                <Link to={result.link} className="text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base">
                  {result.title}
                </Link>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">{result.snippet}</p>
              </div>
            ))}
          </div>
        );
      default:
        return <p className="whitespace-pre-wrap text-sm sm:text-base">{message.content}</p>;
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 p-3 sm:p-4 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors z-40"
        aria-label="Open chat"
      >
        <MessageSquare size={20} />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-16 sm:bottom-24 right-4 sm:right-6 w-[calc(100%-2rem)] sm:w-96 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50">
          {/* Header */}
          <div className="bg-primary-600 text-white p-3 sm:p-4 flex justify-between items-center">
            <div className="flex items-center">
              <MessageSquare size={18} className="mr-2" />
              <h3 className="font-semibold text-sm sm:text-base">Assistant Proxy</h3>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div 
            ref={chatContainerRef}
            className="h-80 sm:h-96 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4"
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-2 sm:p-3 ${
                    message.type === 'user'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-900'
                  }`}
                >
                  {renderMessage(message)}
                </div>
              </div>
            ))}

            {/* Contact Form */}
            {showContactForm && (
              <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">
                  Laissez-nous vos coordonnées
                </h4>
                <EmailFormHandler formName="Demande via Chatbot" className="space-y-3 sm:space-y-4" onSubmit={handleContactFormSubmit}>
                  <div className="flex gap-2 sm:gap-4 mb-3 sm:mb-4">
                    <button
                      type="button"
                      onClick={() => setContactForm(prev => ({ ...prev, type: 'individual' }))}
                      className={`flex-1 p-2 rounded-lg border transition-colors text-xs sm:text-sm ${
                        contactForm.type === 'individual'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 text-gray-600'
                      }`}
                    >
                      <User size={14} className="mx-auto mb-1" />
                      <span>Particulier</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setContactForm(prev => ({ ...prev, type: 'business' }))}
                      className={`flex-1 p-2 rounded-lg border transition-colors text-xs sm:text-sm ${
                        contactForm.type === 'business'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 text-gray-600'
                      }`}
                    >
                      <Building2 size={14} className="mx-auto mb-1" />
                      <span>Entreprise</span>
                    </button>
                  </div>

                  <input type="hidden" name="contact_type" value={contactForm.type} />

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      {contactForm.type === 'business' ? 'Nom de l\'entreprise' : 'Nom complet'}
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-xs sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <div className="relative">
                      <Mail size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full pl-8 sm:pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-xs sm:text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Téléphone
                    </label>
                    <div className="relative">
                      <Phone size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={contactForm.phone}
                        onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full pl-8 sm:pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-xs sm:text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <textarea
                      name="message"
                      required
                      value={contactForm.message}
                      onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-xs sm:text-sm"
                      rows={3}
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-xs sm:text-sm"
                  >
                    Envoyer
                  </button>
                </EmailFormHandler>
              </div>
            )}

            {/* Quick Replies */}
            {messages.length > 0 && BOT_RESPONSES[messages[messages.length - 1].content]?.followUp && (
              <div className="flex flex-wrap gap-2 mt-2">
                {BOT_RESPONSES[messages[messages.length - 1].content].followUp?.map((reply) => (
                  <button
                    key={reply.action}
                    onClick={() => handleQuickReply(reply.action)}
                    className="px-2 sm:px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs sm:text-sm hover:bg-primary-200 transition-colors"
                  >
                    {reply.text}
                  </button>
                ))}
              </div>
            )}

            {/* Link to Page */}
            {messages.length > 0 && BOT_RESPONSES[messages[messages.length - 1].content]?.link && (
              <div className="flex justify-center mt-3 sm:mt-4">
                <Link
                  to={BOT_RESPONSES[messages[messages.length - 1].content].link!}
                  className="text-primary-600 hover:text-primary-700 font-medium text-sm sm:text-base"
                >
                  En savoir plus →
                </Link>
              </div>
            )}

            {isTyping && (
              <div className="flex items-center text-gray-500 space-x-2">
                <Loader2 size={14} className="animate-spin" />
                <span className="text-xs sm:text-sm">En train d'écrire...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 sm:p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Tapez votre message..."
                className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim()}
                className="px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={18} />
              </button>
            </div>

            {/* Quick Replies */}
            <div className="flex flex-wrap gap-2 mt-3 sm:mt-4">
              {QUICK_REPLIES.map((reply) => (
                <button
                  key={reply.action}
                  onClick={() => handleQuickReply(reply.action)}
                  className="px-2 sm:px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs sm:text-sm hover:bg-primary-200 transition-colors"
                >
                  {reply.text}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;